import React, { FC, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Table, TableBody, Tooltip, Typography } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";
import { useIntl } from "react-intl";

import { ApiError } from "@framework/provider-api-jwt";
import { useApiCall } from "@framework/react-hooks";
import { DEFAULT_TCI_LEVELS, useSettings } from "@framework/provider-settings";

import { TrainInfoTable } from "../../../../../../../../components/common/tables/information-table/train-info";
import {
  getTciColor,
  resolveTrainModeBorderColor,
} from "../../../../../../../../components/common/main-menu/equipment/icon-color";
import { useAlarmPaletteResolver } from "../../../../../../../../hooks/use-alarm-color";
import { parseTrainColour } from "../../../../../../../../utils";
import { IStationTrainAnalyticsRow, IStationTrainDefectIcon, IStationTrainMeasureRow } from "./interfaces";
import { StationTrainAnalyticsTable } from "./station-train-analytics-table";
import { StationTrainDefectIcons } from "./station-train-defect-icons";
import { StationTrainMeasuresTable } from "./station-train-measures-table";
import { StationTrainTci } from "./station-train-tci";
import { TrainTypeIcon } from "./train-type-icon";
import { DEFAULT_MODE_BORDER_COLOR, resolveTrainTypeIconSrc } from "./train-type-icons";

interface IInfoResponseTrain {
  Name: string;
  Description: string;
  Position: string;
  EquipmentGroup: string;
}

interface IInfoResponsePath {
  idTopLevel: number;
  idParent: number | null;
  Name: string;
}

interface IInfoResponsePoint {
  ControlledParameter: number;
  count: number;
}

interface ITrainResponse {
  train: IInfoResponseTrain[];
  path: IInfoResponsePath[];
  points: IInfoResponsePoint[];
}

interface IPoint {
  name: string;
  value: string;
}

interface IInfoTrain {
  name?: string;
  description: string;
  position: string;
  rank: string;
  station: string;
  totalPoints: number;
  points: IPoint[];
}

export interface IStationTrain {
  alarmStatus?: number;
  analyticsRowIds?: string[];
  analyticsRows?: IStationTrainAnalyticsRow[];
  customTypeTitle?: string | null;
  defectIcons?: Array<IStationTrainDefectIcon | null>;
  description?: string | null;
  equipmentGroupName?: string;
  handleClick: (trainId: number) => (event: React.MouseEvent) => void;
  hasTciParameters: boolean;
  id: number;
  measureRows?: IStationTrainMeasureRow[];
  modeChangedAt?: string | null;
  modeColor?: string | null;
  name: string;
  tciValue?: number | null;
  trainColour?: number;
  trainInstanceNumber?: string | null;
  trainType?: number | null;
}

export const StationTrain: FC<IStationTrain> = props => {
  const {
    alarmStatus = 0,
    analyticsRowIds = [],
    analyticsRows = [],
    customTypeTitle,
    defectIcons = [],
    description,
    equipmentGroupName,
    handleClick,
    hasTciParameters,
    id,
    measureRows = [],
    modeChangedAt,
    modeColor,
    name,
    tciValue,
    trainColour,
    trainInstanceNumber,
    trainType,
  } = props;

  const [info, setInfo] = useState<ITrainResponse | null>(null);
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  const { formatMessage } = useIntl();
  const settings = useSettings();
  const resolveAlarmColor = useAlarmPaletteResolver();
  const abortControllerRef = useRef<AbortController | null>(null);
  const isTooltipOpenRef = useRef(false);
  const isLoadingRef = useRef(false);

  const tciLevels = settings.getTCILevels() ?? DEFAULT_TCI_LEVELS;
  const alarmColor = resolveAlarmColor(alarmStatus);
  const modeBorderColor = resolveTrainModeBorderColor(
    modeChangedAt,
    modeColor,
    resolveAlarmColor,
    DEFAULT_MODE_BORDER_COLOR,
  );
  const hasTciValue = typeof tciValue === "number" && !Number.isNaN(tciValue);
  const tciDotColor = hasTciValue ? getTciColor(tciValue, tciLevels, resolveAlarmColor) : "#8A93A8";
  const equipmentGroupStyle = trainColour ? parseTrainColour(trainColour) : undefined;
  const iconSrc = resolveTrainTypeIconSrc(trainType, customTypeTitle, name, trainInstanceNumber, description);

  const transformTrainInfo = (json: ITrainResponse): IInfoTrain => {
    const train: IInfoResponseTrain = json.train[0];
    const description = train.Description;
    const position = train.Position;
    const rank = train.EquipmentGroup;
    const station = json.path.map(({ Name }) => Name).join(" / ");
    const totalPoints = json.points.reduce((memo, point) => memo + point.count, 0);
    const points = json.points.map(({ ControlledParameter, count }) => ({
      name:
        typeof ControlledParameter === "number"
          ? formatMessage({ id: `pages.journals.popup.train.points.${ControlledParameter}` })
          : "",
      value: `${count}/${totalPoints}`,
    }));

    return {
      name: undefined,
      description,
      position,
      rank,
      station,
      totalPoints,
      points,
    };
  };

  const { fn, isLoading } = useApiCall(
    async (api, { trainId }: { trainId: number }) => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      isLoadingRef.current = true;

      try {
        const json: ITrainResponse = await api.fetchJson({
          url: `/journal/train/${trainId}/popup`,
          method: "GET",
          signal: abortController.signal,
        });

        if (!abortController.signal.aborted && isTooltipOpenRef.current) {
          setInfo(json);
          setInfoError(null);
        }
      } catch (error: unknown) {
        if (abortController.signal.aborted || (error instanceof Error && error.name === "AbortError")) {
          return;
        }

        const apiError = error instanceof ApiError ? error : null;

        if (!isTooltipOpenRef.current) {
          return;
        }

        setInfoError(formatMessage({ id: "pages.journals.reports.tooltipError" }));
        if (apiError?.status === 400) {
          const errors = apiError.getLocalizedValidationErrors ? apiError.getLocalizedValidationErrors() : {};
          console.error("errors", errors);
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        isLoadingRef.current = false;
      }
    },
    { success: false, error: false },
  );

  const getTrainInfo = useDebouncedCallback(async () => {
    if (info || isLoadingRef.current) {
      return;
    }

    await fn(undefined, {
      trainId: id,
    });
  }, 500);

  const onPointsMouseOver = () => {
    isTooltipOpenRef.current = true;
    setInfoOpen(true);
    if (!info && !isLoadingRef.current) {
      void getTrainInfo();
    }
  };

  const onPointsMouseOut = () => {
    isTooltipOpenRef.current = false;
    getTrainInfo.cancel();
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    isLoadingRef.current = false;
    setInfoOpen(false);
    setInfoError(null);
  };

  useEffect(() => {
    return () => {
      isTooltipOpenRef.current = false;
      getTrainInfo.cancel();
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      isLoadingRef.current = false;
    };
  }, [getTrainInfo]);

  return (
    <Tooltip
      arrow
      title={
        <Paper
          elevation={0}
          sx={{
            m: 0,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            boxSizing: "border-box",
            width: "100%",
            minWidth: 0,
            backgroundColor: "transparent",
            wordBreak: "break-word",
          }}
        >
          {infoError ||
            (!info || isLoading ? (
              <CircularProgress size={15} />
            ) : (
              <Table sx={{ width: "100%" }}>
                <TableBody>
                  <TrainInfoTable trainInfo={transformTrainInfo(info)} showTrain={false} />
                </TableBody>
              </Table>
            ))}
        </Paper>
      }
      PopperProps={{
        sx: {
          "& .MuiTooltip-tooltip": {
            backgroundColor: "#ffffff",
            color: "#000000",
            m: 0,
            p: 0,
            minWidth: 400,
            boxSizing: "border-box",
            border: "2px solid #8A93A8",
            boxShadow: "0px 10px 24px rgba(49, 57, 102, 0.32)",
          },
          "& .MuiTooltip-arrow": {
            color: "#FFFFFF",
            fontSize: 20,
            filter: "drop-shadow(0px 2px 2px rgba(49, 57, 102, 0.32))",
            "&::before": {
              boxSizing: "border-box",
              border: "2px solid #8A93A8",
            },
          },
        },
      }}
      open={infoOpen}
      onMouseOver={onPointsMouseOver}
      onMouseOut={onPointsMouseOut}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 320,
          height: 256,
          m: 1,
          px: 1,
          py: 0.5,
          boxSizing: "border-box",
          border: "2px solid #80DCFF",
          borderRadius: "8px",
          cursor: "pointer",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          boxShadow: "0 0 0 1px rgba(128, 220, 255, 0.18)",
          "&:hover": {
            backgroundColor: "#FCFEFF",
          },
        }}
        onClick={handleClick(id)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: 24,
            minWidth: 0,
            width: "100%",
            mb: 0.75,
          }}
        >
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 18,
              lineHeight: "24px",
              fontWeight: 700,
              color: "#363846",
              letterSpacing: "-0.3px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={name}
          >
            {name}
          </Typography>

          {hasTciParameters ? (
            <Box sx={{ width: 68, flexShrink: 0 }}>
              <StationTrainTci color={tciDotColor} value={tciValue} />
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "flex",
            flex: 1,
            minWidth: 0,
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: 144,
              minWidth: 0,
              flexShrink: 0,
            }}
          >
            <TrainTypeIcon
              alarmColor={alarmColor}
              borderColor={modeBorderColor}
              customTypeTitle={customTypeTitle}
              equipmentGroupColor={equipmentGroupStyle?.color}
              equipmentGroupName={equipmentGroupName}
              iconSrc={iconSrc}
            />

            <Box sx={{ mt: 1, width: "100%" }}>
              <StationTrainDefectIcons icons={defectIcons} />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minWidth: 0,
              pt: 0.5,
            }}
          >
            <Box sx={{ minHeight: 100, overflow: "hidden" }}>
              <StationTrainAnalyticsTable rowIds={analyticsRowIds} rows={analyticsRows} />
            </Box>
            <Box sx={{ mt: 1, minHeight: 0, overflow: "hidden", width: "100%" }}>
              <StationTrainMeasuresTable rows={measureRows} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
};
